import { ccfRoutes } from '@app/ccf/ccf-routes';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import PageHeaderToolbar from '@app/components/layout/page/components/page-header-toolbar';
import Page from '@app/components/layout/page/page';
import { useCCFUpsellDialog } from '@app/hooks/use-ccf-upsell-dialog';
import { useUpdateCategoryStatus } from '@shared/api/hooks/ccf/reports';
import { useGetSecrConfig } from '@shared/api/hooks/ccf/secr';
import {
  CompanyReportCategoryStatusStatusEnum,
  CompanyReportStatusEnum,
} from '@shared/api/types';
import { SupportButton } from '@shared/components/buttons/support-button';
import { Icons } from '@shared/components/content/icons';
import Scope3CategoryBadge from '@shared/components/content/scope-3-category-badge';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { useToast } from '@shared/components/ui/use-toast';
import { formatDate } from 'date-fns';
import {
  generatePath,
  matchPath,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useCCF } from './ccf-context';
import { ScopeCategory, useScopeCategories } from './scopes';
import { useCategory } from './utils';

function CCFSummaryHeader() {
  const navigate = useNavigate();
  const {
    title,
    startDate,
    endDate,
    status: publicationStatus,
    categories,
    uuid,
  } = useCCF();

  const reportUnstarted = categories.every(
    ({ hasActivityData }) => !hasActivityData
  );
  const disablePublish =
    publicationStatus === CompanyReportStatusEnum.published || reportUnstarted;

  return (
    <PageHeader
      name={title!}
      headingTestId="report-hub-heading"
      description={`${formatDate(startDate, 'do MMMM yyyy')} - ${formatDate(endDate, 'do MMMM yyyy')}`}
      toolbar={
        <PageHeaderToolbar>
          <SimpleTooltip
            disabled={!disablePublish}
            trigger={
              <Button
                onClick={() => {
                  navigate(
                    generatePath(ccfRoutes.PUBLISH, {
                      report_uuid: uuid,
                    })
                  );
                }}
                disabled={disablePublish}
                data-testid="hub-publish-button"
              >
                Publish
              </Button>
            }
            content={
              reportUnstarted
                ? 'No data to publish'
                : publicationStatus === CompanyReportStatusEnum.published
                  ? 'No changes to publish'
                  : ''
            }
          />
        </PageHeaderToolbar>
      }
      breadcrumbs={[
        {
          label: 'Company reports',
          url: generatePath(ccfRoutes.COMPANY_REPORTS),
        },
      ]}
    />
  );
}

function CCFSecrHeader() {
  const navigate = useNavigate();
  const { title, uuid, firstPublishedAt } = useCCF();
  const { data: secrConfig } = useGetSecrConfig(uuid);
  const summaryUrl = generatePath(ccfRoutes.COMPANY_REPORT, {
    report_uuid: uuid,
  });
  const isConfigComplete = secrConfig?.isConfigComplete ?? false;
  return (
    <PageHeader
      name="SECR configuration"
      toolbar={
        <PageHeaderToolbar>
          <div className="space-x-2 flex items-center">
            <Button
              icon={<Icons.chevronLeft className="size-4" />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(summaryUrl)}
            >
              Back to summary
            </Button>
            <SupportButton article="secr" size="sm" />
            <SimpleTooltip
              disabled={!!firstPublishedAt && isConfigComplete}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!firstPublishedAt || !isConfigComplete}
                  onClick={() =>
                    navigate(
                      generatePath(ccfRoutes.SECR_REPORT, {
                        report_uuid: uuid,
                      })
                    )
                  }
                >
                  View SECR report
                </Button>
              }
              content={
                !firstPublishedAt
                  ? 'Publish your company report to view the SECR report'
                  : 'Complete the SECR configuration to view the report'
              }
            />
          </div>
        </PageHeaderToolbar>
      }
      breadcrumbs={[
        {
          label: 'Company reports',
          url: generatePath(ccfRoutes.COMPANY_REPORTS),
        },
        {
          label: title!,
          url: summaryUrl,
        },
      ]}
    />
  );
}

function CCFCategoryHeader({ category }: { category: ScopeCategory }) {
  const navigate = useNavigate();
  const { title, uuid } = useCCF();
  const categoryData = useCategory(category.slug);
  const { toast } = useToast();
  const { mutateAsync: updateCategoryStatus, isLoading: isUpdating } =
    useUpdateCategoryStatus();

  const summaryUrl = generatePath(ccfRoutes.COMPANY_REPORT, {
    report_uuid: uuid,
  });

  const isComplete =
    categoryData.status === CompanyReportCategoryStatusStatusEnum.complete;
  const hasActivityData = categoryData.hasActivityData;

  const handleToggleComplete = async () => {
    try {
      const newStatus = isComplete
        ? CompanyReportCategoryStatusStatusEnum.included
        : CompanyReportCategoryStatusStatusEnum.complete;

      await updateCategoryStatus({
        companyReportUuid: uuid!,
        data: {
          activityType: category.slug,
          status: newStatus,
        },
      });
    } catch (error) {
      toast({
        title: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  return (
    <PageHeader
      name={category.name}
      nameActions={
        <div className="flex gap-2">
          <Badge variant="primary">Scope {category.scope}</Badge>
          {category.scope3Category && (
            <Scope3CategoryBadge category={category.scope3Category} />
          )}
        </div>
      }
      toolbar={
        <PageHeaderToolbar>
          <div className="flex items-center space-x-2">
            <Button
              icon={<Icons.chevronLeft className="size-4" />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(summaryUrl)}
            >
              Back to summary
            </Button>
            {category.supportArticle && (
              <SupportButton article={category.supportArticle} size="sm" />
            )}
            {hasActivityData && (
              <Button
                variant="secondary"
                disabled={isUpdating}
                onClick={handleToggleComplete}
                className="pl-2"
                size="sm"
                icon={
                  isComplete ? (
                    <Icons.undo className="size-4" />
                  ) : (
                    <Icons.check className="size-4" />
                  )
                }
              >
                {isComplete ? 'Mark as incomplete' : 'Mark as complete'}
              </Button>
            )}
          </div>
        </PageHeaderToolbar>
      }
      breadcrumbs={[
        {
          label: 'Company reports',
          url: generatePath(ccfRoutes.COMPANY_REPORTS),
        },
        {
          label: title!,
          url: summaryUrl,
        },
      ]}
    />
  );
}

export default function CCFLayout() {
  const location = useLocation();
  const { UpsellDialogComponent } = useCCFUpsellDialog();
  const { reportStatus } = useCCF();
  const categories = useScopeCategories();

  const activeCategory = categories.find((category) =>
    matchPath(category.href, location.pathname)
  );
  const isSecrConfig = matchPath(
    ccfRoutes.SECR_CONFIGURATION,
    location.pathname
  );

  return (
    <Page
      name="Company carbon footprint hub"
      page="layout"
      status={reportStatus}
      flexFullHeight
    >
      {activeCategory ? (
        <CCFCategoryHeader category={activeCategory} />
      ) : isSecrConfig ? (
        <CCFSecrHeader />
      ) : (
        <CCFSummaryHeader />
      )}
      <Outlet />
      {UpsellDialogComponent}
    </Page>
  );
}
